import {
  Autocomplete,
  Button,
  Divider,
  Flex,
  Grid,
  ScrollView,
  SelectField,
} from "@aws-amplify/ui-react";
import LocalizedStrings from "react-localization";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import {
  Add,
  ContentCopy,
  Edit,
  ReplayCircleFilled,
  Save,
  Share,
} from "@mui/icons-material";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { Resizable } from "react-resizable";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";
import SharedIcon from "@mui/icons-material/FolderShared";

import useDeviceChart from "hooks/devices/useDeviceChart";
import DeviceChart from "components/devices/DeviceChart";
import { Devices } from "modules/devices";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  dashboardsByOwnerID,
  getDashboard,
  sharedDashboardsByUserID,
} from "graphql/queries";
import "react-resizable/css/styles.css";
import { globalStrings } from "config/translations/es";
import DashboardForm from "components/dashboards/DashboardForm";
import {
  createDashboard,
  deleteDashboard,
  updateDashboard,
} from "graphql/mutations";
import { configInitialValues } from "config/constants";
import DashboardCardForm from "components/dashboards/DashboardCardForm";
import { getDeviceName } from "graphql/custom-queries";
import SharedDashboardForm from "components/dashboards/SharedDashboardForm";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Dashboard = () => {
  const {
    me: { id: userId, companyID: myCompanyId },
  } = useContext(LayoutContext);
  const [dashboards, setDashboards] = useState([]);
  const [cardDeviceName, setCardDeviceName] = useState("");
  const [selectedDashboard, setSelectedDashboard] = useState({});
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const [modalDashboard, setModalDashboard] = useState({
    visible: false,
    action: "create",
    details: null,
  });
  const [modalShared, setModalShared] = useState({
    visible: false,
  });
  const [modalCard, setModalCard] = useState({
    visible: false,
    action: "add",
    index: "",
    config: configInitialValues,
  });
  const [
    devicesChart,
    setInitialValues,
    { onAdd, onUpdate, onDelete, onSort, onResize, onAutoRefreshAll },
  ] = useDeviceChart();
  const isAutoRefreshAll = useMemo(
    () =>
      devicesChart?.length > 0 &&
      devicesChart?.every((devCh) => !!devCh.autoRefresh),
    [devicesChart]
  );

  const renderOption = (option) => {
    const { label, isOwner } = option;
    return (
      <Flex gap="5px" alignItems="center">
        {!isOwner && <SharedIcon />}
        <span>{label}</span>
      </Flex>
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(strings.textCopied);
  };

  const onSubmitDashboard = (newItem) => {
    if (modalDashboard.action === "create") onCreateDashboard(newItem);
    else if (modalDashboard.action === "view") onSaveDashboard(newItem);
  };

  const onDeleteDashboard = async () => {
    try {
      const { id } = selectedDashboard;

      await API.graphql({
        query: deleteDashboard.replaceAll("__typename", ""),
        variables: {
          input: {
            id,
          },
        },
      });

      toast.success(strings.successDeleted);
      setSelectedDashboard({});
      setInitialValues([]);
      onCloseModalDashboard();
      fetchDashboardsByUser();
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  };

  const onCreateDashboard = async (newItem) => {
    try {
      const { name, description } = newItem;

      const newDashboard = (
        await API.graphql({
          query: createDashboard.replaceAll("__typename", ""),
          variables: {
            input: {
              name,
              description,
              ownerID: userId,
            },
          },
        })
      )?.data?.createDashboard;

      toast.success(strings.successCreated);
      setSelectedDashboard({ ...newDashboard, isOwner: true });
      setInitialValues([]);
      onCloseModalDashboard();
      fetchDashboardsByUser();
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  };

  const onSaveDashboard = async (newItem) => {
    try {
      const { id, name, description, isOwner, sharedPermissions } =
        selectedDashboard;

      const dashboardUpdated = (
        await API.graphql({
          query: updateDashboard.replaceAll("__typename", ""),
          variables: {
            input: {
              id,
              name: newItem?.name ?? name,
              description: newItem?.description ?? description,
              chartDetails: JSON.stringify(devicesChart),
            },
          },
        })
      )?.data?.updateDashboard;

      toast.success(strings.successSaved);
      setSelectedDashboard({ ...dashboardUpdated, isOwner, sharedPermissions });
      onCloseModalDashboard();
      fetchDashboardsByUser();
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  };

  const onSaveCard = (values) => {
    const finalVal = {
      ...values,
      companyId: searchBy.company.id,
      branchId: searchBy.branch.id,
    };

    if (finalVal.graphBy === "company" && !finalVal.companyId) {
      toast.error(strings.responses.companyIsRequired);
      return;
    }
    if (finalVal.graphBy === "branch" && !finalVal.branchId) {
      toast.error(strings.responses.branchIsRequired);
      return;
    }
    if (
      (finalVal.graphBy === "device" || !finalVal.graphBy) &&
      !finalVal.deviceId
    ) {
      toast.error(strings.responses.deviceIsRequired);
      return;
    }

    if (modalCard.action === "add") onAdd(finalVal);
    else if (modalCard.action === "update") onUpdate(modalCard.index, finalVal);
    onCloseModalCard();
  };

  const resetSearchBy = () => {
    setSearchBy({
      company: {
        id: myCompanyId || "",
        display: "",
      },
      branch: {
        id: "",
        display: "",
      },
    });
  };

  const onCloseModalCard = () => {
    setModalCard({
      visible: false,
      action: "add",
      index: "",
      config: configInitialValues,
    });
    resetSearchBy();
  };

  const onOpenModalCard = (action, item) => {
    const finalConfig = item ?? configInitialValues;
    setModalCard((prevSt) => ({
      visible: !prevSt.visible,
      action,
      index: item?.index,
      config: finalConfig,
    }));
    if (item?.deviceId) fetchDeviceName(item.deviceId);
    setSearchBy({
      company: {
        id: finalConfig?.companyId || myCompanyId || "",
        display: "",
      },
      branch: {
        id: finalConfig?.branchId,
        display: "",
      },
    });
  };

  const onCloseModalDashboard = () => {
    setModalDashboard({ visible: false, action: "create" });
  };

  const onCloseModalShared = () => {
    setModalShared({ visible: false });
  };

  const onOpenModalDashboard = (action, details) => {
    setModalDashboard((prevSt) => ({
      visible: !prevSt.visible,
      action,
      details,
    }));
  };

  const onOpenModalShared = () => {
    setModalShared((prevSt) => ({
      visible: !prevSt.visible,
    }));
  };

  const onSelectDashboard = (id) => {
    const { name, description, chartDetails, isOwner, sharedPermissions } =
      dashboards.find((dashItem) => dashItem.id === id);
    const chartDetailsParse = chartDetails ? JSON.parse(chartDetails) : [];
    setSelectedDashboard({
      id,
      name,
      description,
      chartDetails: chartDetailsParse,
      isOwner,
      sharedPermissions,
    });
    setInitialValues(chartDetailsParse);
  };

  const onClearDashboard = () => {
    setSelectedDashboard({});
    setInitialValues([]);
  };

  const onSelectDevice = (deviceItem) => {
    const { deviceId, deviceName } = deviceItem;
    setModalCard((prevSt) => ({
      ...prevSt,
      config: { ...configInitialValues, deviceId },
    }));
    setCardDeviceName(deviceName);
  };

  const onSelectGraphBy = (e) => {
    const { value } = e.target;
    setModalCard((prevSt) => ({
      ...prevSt,
      config: { ...configInitialValues, graphBy: value },
    }));
    resetSearchBy();
  };

  async function fetchDashboardsByUser() {
    try {
      const dashboardsData = await API.graphql(
        graphqlOperation(dashboardsByOwnerID, {
          ownerID: userId,
        })
      );
      const dashboardsItems = dashboardsData.data.dashboardsByOwnerID.items;

      const sharedDashboardsData = await API.graphql(
        graphqlOperation(sharedDashboardsByUserID, {
          userID: userId,
        })
      );
      const sharedDashboardsItems =
        sharedDashboardsData.data.sharedDashboardsByUserID.items;
      let anotherDashboardsItems = [];

      if (sharedDashboardsItems?.length > 0) {
        await Promise.all(
          sharedDashboardsItems.map(async (sh) => {
            const anotherDashboardData = await API.graphql(
              graphqlOperation(getDashboard, {
                id: sh.dashboardID,
              })
            );
            anotherDashboardsItems.push(anotherDashboardData.data.getDashboard);
          }) ?? []
        );
      }

      setDashboards([
        ...dashboardsItems?.map((dash) => ({ ...dash, isOwner: true })),
        ...anotherDashboardsItems?.map((dash) => ({
          ...dash,
          isOwner: false,
          sharedPermissions:
            sharedDashboardsItems?.find(
              (sharedDash) => sharedDash.dashboardID === dash.id
            )?.permissions ?? [],
        })),
      ]);
    } catch (err) {
      toast.error(strings.responses.errorFetchData);
    }
  }

  async function fetchDeviceName(id) {
    const deviceData = await API.graphql(
      graphqlOperation(getDeviceName, {
        id,
      })
    );
    const deviceItem = deviceData.data.getDevice;
    setCardDeviceName(deviceItem?.name ?? "");
  }

  useEffect(() => {
    fetchDashboardsByUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Grid
        style={{ marginTop: "15px", marginBottom: "10px" }}
        templateColumns={{ base: "1fr", large: "4fr 1fr" }}
        rowGap="15px"
        columnGap="15px"
      >
        <Flex style={{ alignItems: "center" }}>
          <div style={{ width: "100%" }}>
            <Autocomplete
              name="dashboard"
              label={strings.fields.dashboard.label}
              placeholder={strings.fields.dashboard.placeholder}
              options={dashboards.map((dashboardRec) => ({
                id: dashboardRec.id,
                label: dashboardRec.name,
                isOwner: dashboardRec.isOwner,
              }))}
              renderOption={renderOption}
              onClear={onClearDashboard}
              onSelect={({ id }) => onSelectDashboard(id)}
              menuSlots={{ Empty: globalStrings.noResults }}
            ></Autocomplete>
          </div>
          {selectedDashboard.id && selectedDashboard.isOwner && (
            <Edit
              onClick={() => onOpenModalDashboard("view", selectedDashboard)}
              style={styles.iconAction}
              color="action"
            ></Edit>
          )}
        </Flex>
        <Flex style={{ alignItems: "center", justifyContent: "end" }}>
          <Button
            variation="primary"
            onClick={() => onOpenModalDashboard("create")}
          >
            {strings.buttonCreate}
          </Button>
          {selectedDashboard.id && (
            <>
              <ReplayCircleFilled
                onClick={() => onAutoRefreshAll(!isAutoRefreshAll)}
                style={styles.iconAction}
                color={!isAutoRefreshAll ? "action" : "primary"}
              ></ReplayCircleFilled>
              {(selectedDashboard.sharedPermissions?.some(
                (per) => per === "edit"
              ) ||
                selectedDashboard.isOwner) && (
                <Save
                  onClick={onSaveDashboard}
                  style={styles.iconAction}
                  color="action"
                ></Save>
              )}
              {(selectedDashboard.sharedPermissions?.some(
                (per) => per === "share"
              ) ||
                selectedDashboard.isOwner) && (
                <Share
                  onClick={onOpenModalShared}
                  style={styles.iconAction}
                  color="action"
                ></Share>
              )}
            </>
          )}
        </Flex>
      </Grid>
      {selectedDashboard.id && (
        <Grid className="p-3" padding="20px" rowGap="30px">
          <Flex
            wrap="wrap"
            rowGap="30px"
            columnGap="30px"
            alignItems="flex-start"
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={(event) => onSort(event.active.id, event.over.id)}
            >
              <SortableContext items={devicesChart.map((devCh) => devCh.index)}>
                {devicesChart?.map((item) => (
                  <Resizable
                    minConstraints={[250, 100]}
                    draggableOpts={{ grid: [5, 5] }}
                    key={item.index}
                    height={item.height}
                    width={item.width}
                    onResize={(_, { size }) => onResize(item.index, size)}
                  >
                    <div>
                      <DeviceChart
                        deviceModel={item}
                        onDelete={
                          (selectedDashboard.sharedPermissions?.some(
                            (per) => per === "edit"
                          ) ||
                            selectedDashboard.isOwner) &&
                          (() => onDelete(item.index))
                        }
                        onUpdate={
                          (selectedDashboard.sharedPermissions?.some(
                            (per) => per === "edit"
                          ) ||
                            selectedDashboard.isOwner) &&
                          (() => onOpenModalCard("update", item))
                        }
                        draggable={
                          selectedDashboard.sharedPermissions?.some(
                            (per) => per === "edit"
                          ) || selectedDashboard.isOwner
                        }
                      />
                    </div>
                  </Resizable>
                ))}
              </SortableContext>
            </DndContext>
            <CCard className="p-2">
              <CCardBody
                style={styles.addBox}
                onClick={() => onOpenModalCard("add")}
              >
                <Add style={styles.addIcon} color="action"></Add>
                {strings.add}
              </CCardBody>
            </CCard>
          </Flex>
        </Grid>
      )}
      <CModal
        size="xl"
        alignment="center"
        visible={modalCard.visible}
        portal={false}
        onClose={onCloseModalCard}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.configCard}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <Grid
              className="mb-3"
              templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr 1fr" }}
              rowGap="15px"
              columnGap="15px"
            >
              <SelectField
                name="graphBy"
                value={modalCard.config?.graphBy}
                label={strings.fields.graphBy.label}
                onChange={onSelectGraphBy}
              >
                {Object.entries(strings.fields.graphBy.options).map((val) => (
                  <option value={val[0]}>{val[1]}</option>
                ))}
              </SelectField>
            </Grid>
            {modalCard.config?.graphBy !== "branch" &&
            modalCard.config?.graphBy !== "company" ? (
              <Devices onSelectDevice={onSelectDevice} />
            ) : (
              <div className="px-3">
                <Grid
                  className="mb-3"
                  templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
                  rowGap="15px"
                  columnGap="15px"
                >
                  <CompanyBranchFilter
                    searchBy={searchBy}
                    setSearchBy={setSearchBy}
                    onlyCompany={modalCard.config?.graphBy === "company"}
                    withLabels
                  />
                </Grid>
              </div>
            )}
            <>
              {modalCard.config?.deviceId && (
                <>
                  <div
                    className="text-muted"
                    style={{ paddingLeft: "20px", paddingTop: "10px" }}
                  >
                    <b>{strings.modalCard.deviceId}:</b>{" "}
                    {modalCard.config?.deviceId}{" "}
                    <ContentCopy
                      onClick={() =>
                        copyToClipboard(modalCard.config?.deviceId)
                      }
                      style={styles.copyIcon}
                      color="info"
                    ></ContentCopy>
                  </div>
                  <div
                    className="text-muted"
                    style={{ paddingLeft: "20px", paddingTop: "10px" }}
                  >
                    <b>{strings.modalCard.deviceName}:</b> {cardDeviceName}
                  </div>
                </>
              )}
              {(modalCard.config?.deviceId ||
                modalCard.config?.graphBy === "branch" ||
                modalCard.config?.graphBy === "company") && (
                <>
                  <Divider className="mt-3 mb-1" />
                  <DashboardCardForm
                    onSubmit={onSaveCard}
                    initialValues={modalCard.config}
                  />
                </>
              )}
            </>
          </ScrollView>
        </CModalBody>
      </CModal>
      <CModal
        size="lg"
        alignment="center"
        visible={modalDashboard.visible}
        onClose={onCloseModalDashboard}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {modalDashboard.action === "create"
              ? strings.modalDashboard.create
              : strings.modalDashboard.edit}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DashboardForm
            onSubmit={onSubmitDashboard}
            onDelete={onDeleteDashboard}
            type={modalDashboard.action}
            initialValues={modalDashboard.details}
          />
        </CModalBody>
      </CModal>
      <CModal
        alignment="center"
        visible={modalShared.visible}
        onClose={onCloseModalShared}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.modalShared.title} "{selectedDashboard.name}"
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <SharedDashboardForm
              dashboardID={selectedDashboard.id}
              onSuccess={onCloseModalShared}
              isOwner={selectedDashboard.isOwner}
              permissions={selectedDashboard.sharedPermissions}
            />
          </ScrollView>
        </CModalBody>
      </CModal>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Tableros Personalizados",
    chart: {
      title: "Corriente",
    },
    current: "Corriente",
    responses: {
      errorFetchData: "Error al consultar datos",
      companyIsRequired: "La empresa es requerida",
      branchIsRequired: "La sede es requerida",
      deviceIsRequired: "El dispositivo es requerido",
    },
    add: "Agregar",
    configCard: "Configuración de Tarjeta",
    fields: {
      dashboard: {
        label: "Tableros",
        placeholder: "Tableros de consulta...",
      },
      graphBy: {
        label: "Graficar por",
        options: {
          device: "Dispositivo",
          branch: "Sede",
          company: "Empresa",
        },
      },
    },
    buttonCreate: "Crear Tablero",
    modalDashboard: {
      create: "Crear Tablero",
      edit: "Editar Tablero",
    },
    modalShared: {
      title: "Compartir Tablero",
    },
    successCreated: "Tablero creado con éxito",
    successDeleted: "Tablero eliminado con éxito",
    successSaved: "Tablero guardado con éxito",
    selectDateRange: "Seleccionar Rango de Fecha",
    modalCard: {
      deviceId: "ID del dispositivo seleccionado",
      deviceName: "Nombre del dispositivo seleccionado",
    },
    textCopied: "Texto copiado",
  },
});

const styles = {
  addBox: {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  addIcon: {
    fontSize: "30px",
  },
  iconAction: {
    cursor: "pointer",
    fontSize: "30px",
  },
  copyIcon: {
    cursor: "pointer",
    fontSize: "18px",
  },
};

export default Dashboard;
